import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Badge, Button, Icon, Tooltip } from '@syneto/compass-react';
import { connect } from 'react-redux';
import Form from '../../../../components/forms/form';
import { computeAddonEndDate, isAddonExpired } from '../../../../helpers/addonPeriod';
import { applyForever, grantKind, isReadOnlyGrant, readsAsForever } from '../../../../helpers/grantRows';

const DATE_FORMAT = 'DD/MM/YYYY';

const KIND_APPEARANCE = { Temporary: 'info', Trial: 'warning', License: 'secondary' };

const DURATION_TYPE_OPTIONS = [
	{ value: 'days', label: 'Days' },
	{ value: 'months', label: 'Months' },
	{ value: 'years', label: 'Years' }
];

// Calendar dates stored at UTC midnight (see client/helpers/addonPeriod.js): shown in UTC so the day does not shift.
const formatDate = (value) => {
	const date = value ? moment.utc(value) : null;

	return date && date.isValid() ? date.format(DATE_FORMAT) : null;
};

/*
 * Whether the row has a start date worth showing: a dated row's own, or the activation date the server set on a saved
 * start-on-activation row. A start-on-activation row that was never saved has no start yet, whatever stale date it
 * still carries from before the box was ticked.
 */
export const hasKnownStart = (addon) => {
	return Boolean(addon.startDate) && (!addon.startOnActivation || Boolean(addon.id));
};

const isForeverGrant = (addon) => {
	return Boolean(addon.isForever) || readsAsForever(addon);
};

/*
 * The Period column of the overview: the row's dates in one line, with the duration under it. The 99-year duration
 * of a forever row and the placeholder duration of a "follows maintenance" row are never shown.
 */
export const describeGrantPeriod = (addon) => {
	if (addon.followsMaintenance) {
		const end = formatDate(addon.endDate);

		return { period: 'Follows maintenance', detail: end ? `Until ${end}` : '' };
	}

	const forever = isForeverGrant(addon);
	const duration = addon.duration ? `${addon.duration} ${addon.durationType}` : '';

	if (!hasKnownStart(addon)) {
		if (addon.startOnActivation) {
			return { period: 'Starts on activation', detail: forever ? 'Never ends' : (duration ? `${duration} after activation` : '') };
		}

		return { period: 'Start date not set', detail: forever ? 'Never ends' : duration };
	}

	const start = formatDate(addon.startDate);

	if (forever) {
		return { period: `From ${start}`, detail: 'Never ends' };
	}

	const computedEnd = computeAddonEndDate(addon.startDate, addon.duration, addon.durationType);
	const end = formatDate(addon.endDate) || (computedEnd ? computedEnd.format(DATE_FORMAT) : 'N/A');

	return { period: `${start} → ${end}`, detail: duration };
};

/*
 * The Status column of the overview, from the row's dates alone; a row the validation refuses is labelled by the form
 * instead (see ProductAddonRow). `now` is for tests.
 */
export const describeGrantStatus = (addon, now) => {
	if (addon.followsMaintenance) {
		return { label: 'Follows maintenance', appearance: 'secondary' };
	}
	if (isAddonExpired(addon.endDate, now)) {
		return { label: 'Expired', appearance: 'danger', hint: 'Expired. Please renew this license' };
	}
	if (!hasKnownStart(addon)) {
		return addon.startOnActivation ? { label: 'Pending activation', appearance: 'info' } : { label: 'Not started', appearance: 'secondary' };
	}

	const today = (now ? moment.utc(now) : moment.utc()).startOf('day');

	if (moment.utc(addon.startDate).startOf('day').isAfter(today)) {
		return { label: 'Scheduled', appearance: 'info' };
	}

	return { label: 'Active', appearance: 'success' };
};

const describeTier = (addon, license) => {
	const tier = addon.tier || license?.addonTiers?.find((item) => { return item.id === addon.tierId; });

	return tier ? `${tier.name}${tier.unitOfMeasurement ? ` (${tier.unitOfMeasurement.symbol})` : ''}` : '';
};

/*
 * The fields of the one row being edited. They are redux-form fields, so they read and write the product form's
 * `productAddons[index]`, while `changeAddon` keeps the form's own list in step.
 */
const ProductAddonEditor = ({ addon, index, license, availableProductAddons, changeAddon, changeProp, onDone }) => {
	// A start-on-activation row has an end to show once the appliance is activated: the server then set its start.
	const computedEndDate = hasKnownStart(addon) || !addon.startOnActivation ? computeAddonEndDate(addon.startDate, addon.duration, addon.durationType) : null;
	const endDate = addon.isForever ? 'Forever' : (addon.startOnActivation && !hasKnownStart(addon) ? 'Starts on activation' : (computedEndDate ? computedEndDate.format(DATE_FORMAT) : 'N/A'));

	return (
		<div className="product-addon-editor border rounded bg-light p-3 mb-3">
			<Row>
				<Col xs={5}>
					<Form.select
						label="License"
						name={`productAddons[${index}].addonId`}
						options={(availableProductAddons.map((item) => { return { value: item.id, label: item.name }; }))}
						emptyOption="Please select"
						onValueChange={(value) => { changeAddon({ ...addon, addonId: value }); }}
						required
					/>
				</Col>
				<Col xs={3}>
					<Form.select
						label="Tier"
						disabled={!addon.addonId}
						name={`productAddons[${index}].tierId`}
						options={(license?.addonTiers?.map((tier) => {
							const unitInfo = tier.unitOfMeasurement ? ` (${tier.unitOfMeasurement.symbol})` : '';
							return { value: tier.id, label: `${tier.name}${unitInfo}` };
						}) ?? [])}
						emptyOption="No tier"
						onValueChange={(value) => { changeAddon({ ...addon, tierId: value }); }}
					/>
				</Col>
				<Col xs={4} className="d-flex align-items-end gap-4">
					<Form.checkbox
						label="Forever"
						name={`productAddons[${index}].isForever`}
						onValueChange={(value) => { changeAddon(applyForever(addon, Boolean(value))); }}
					/>
					<Form.checkbox
						label="Start on activation"
						name={`productAddons[${index}].startOnActivation`}
						onValueChange={(value) => { changeAddon({ ...addon, startOnActivation: value }); }}
					/>
				</Col>
			</Row>
			<Row className="align-items-end">
				<Col xs={3}>
					<Form.datepicker
						label="Start date"
						id={`productAddons[${index}].startDate`}
						name={`productAddons[${index}].startDate`}
						placeholderText="Not set"
						placement="left"
						initValue={hasKnownStart(addon) ? moment(addon.startDate).format(DATE_FORMAT) : ''}
						autoComplete="off"
						onValueChange={(value) => {
							changeAddon({ ...addon, startDate: (value && !addon.startOnActivation ? moment(value).tz('UTC', true).startOf('day').toISOString() : '') });
						}}
						changeProp={changeProp}
						key={addon.startOnActivation ? 'empty' : `productAddons[${index}].startDate`}
						disabled={addon.startOnActivation}
					/>
				</Col>
				{!addon.isForever && (
					// The duration of a forever row is a placeholder (99 years) the server stores; it is never shown. The unit
					// is the duration's suffix, "5 Months" under one label, as it is read.
					<Col xs={4}>
						<label className="form-label" htmlFor={`productAddons[${index}].duration`}>Duration*</label>
						<div className="d-flex gap-2 align-items-start">
							<div style={{ flex: '0 0 40%' }}>
								<Form.number id={`productAddons[${index}].duration`} name={`productAddons[${index}].duration`} onChange={(e) => { changeAddon({ ...addon, duration: e.currentTarget.value }); }} required />
							</div>
							<div style={{ flex: '1 1 auto' }}>
								<Form.select
									name={`productAddons[${index}].durationType`}
									options={DURATION_TYPE_OPTIONS}
									onValueChange={(value) => { changeAddon({ ...addon, durationType: value }); }}
								/>
							</div>
						</div>
					</Col>
				)}
				<Col xs={3}>
					<div className="mb-3">
						<label className="form-label">End date</label>
						<div className="form-control-plaintext">{endDate}</div>
					</div>
				</Col>
				<Col className="text-end">
					<Button role="secondary" className="mb-3" onClick={onDone}>Done</Button>
				</Col>
			</Row>
		</div>
	);
};

/*
 * One license of the product form's overview: a line of text that says what the row grants and when, with the
 * controls to edit or remove it. Only the row the form has opened shows its fields, so the list stays readable
 * however many licenses a product has. A row Central made itself (an approved temporary license, the 60-day trial,
 * a license that follows the maintenance) is locked: nothing on it can be edited or removed, and the save sends it
 * back as it came.
 */
export const ProductAddonRow = (props) => {
	const { productAddons: availableProductAddons, changeProp, removeAddon, changeAddon, addon, index, isOpen, onToggle, errors } = props;
	const license = availableProductAddons.find((item) => { return item.id === addon.addonId; });
	const licenseName = addon.addon?.name || license?.name || (addon.addonId ? 'Unknown license' : 'New license');
	const tierName = describeTier(addon, license);
	const readOnly = isReadOnlyGrant(addon);
	const kind = grantKind(addon);
	const { period, detail } = describeGrantPeriod(addon);
	const errorMessages = Object.values(errors || {}).filter(Boolean);
	const status = errorMessages.length > 0 ? { label: 'Incomplete', appearance: 'warning', hint: errorMessages.join(' ') } : describeGrantStatus(addon);
	const open = Boolean(isOpen) && !readOnly;
	const statusBadge = <Badge appearance={status.appearance}>{status.label}</Badge>;

	return (
		<div className={readOnly ? 'product-addon product-addon-read-only' : 'product-addon'}>
			<Row className="align-items-center py-2 border-top">
				<Col xs={3}>
					<span className="fw-bold me-2">{licenseName}</span>
					<Tooltip text={addon.addonType} placement="top" trigger="hover">
						<Badge appearance={KIND_APPEARANCE[kind]}>{kind}</Badge>
					</Tooltip>
				</Col>
				<Col xs={3}>
					<div>{period}</div>
					{detail && <div className="text-muted small">{detail}</div>}
				</Col>
				<Col xs={2}>{tierName || <span className="text-muted">No tier</span>}</Col>
				<Col xs={2}>
					{status.hint ? <Tooltip text={status.hint} placement="top" trigger="hover">{statusBadge}</Tooltip> : statusBadge}
				</Col>
				<Col xs={2} className="text-end">
					{readOnly ? (
						<Tooltip text="Central made this license itself. It cannot be edited or removed here." placement="top" trigger="hover">
							<Icon name="fa fa-lock" className="text-muted" />
						</Tooltip>
					) : (
						<>
							<Button role="tertiary" className="py-0" aria-label={open ? 'Close the license' : 'Edit the license'} onClick={onToggle}>
								<Icon name={open ? 'fa fa-chevron-up' : 'fa fa-pencil'} className="text-muted" />
							</Button>
							<Button role="tertiary" className="py-0" aria-label="Remove the license" onClick={() => { removeAddon(addon.uuid); }}>
								<Icon name="fa fa-trash" className="text-muted" />
							</Button>
						</>
					)}
				</Col>
			</Row>
			{open && (
				<ProductAddonEditor
					addon={addon}
					index={index}
					license={license}
					availableProductAddons={availableProductAddons}
					changeAddon={changeAddon}
					changeProp={changeProp}
					onDone={onToggle}
				/>
			)}
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		productAddons: state.productAddons.data,
	};
};

export default connect(mapStateToProps, null)(ProductAddonRow);

ProductAddonRow.propTypes = {
	addon: PropTypes.object.isRequired,
	index: PropTypes.number.isRequired,
	isOpen: PropTypes.bool,
	onToggle: PropTypes.func.isRequired,
	errors: PropTypes.object,
	removeAddon: PropTypes.func.isRequired,
	changeAddon: PropTypes.func.isRequired,
	changeProp: PropTypes.func.isRequired,
};
